import { Button, Input, Select, Typography } from 'antd';
import {
	PlusOutlined,
	FilterOutlined,
	InfoCircleOutlined,
	AppstoreOutlined,
	UnorderedListOutlined,
	UserOutlined,
	SettingOutlined,
	CloseOutlined,
} from '@ant-design/icons';
import { THeaderSectionProps, TFilterView } from '@/types/TSmartCRM';
import { PLACEHOLDERS } from 'src/constants/smartCRM';
import { ETabs, EViewType } from 'src/enums/ESmartCRM';

const { Search } = Input;
const { Text } = Typography;

interface ExtendedHeaderSectionProps extends THeaderSectionProps {
	onCustomersTab?: () => void;
	activeTab?: ETabs;
	filteredCount?: number;
	filterViews?: TFilterView[];
	activeFilterView?: string | null;
	onFilterViewChange?: (viewId: string | null) => void;
	onCloseFilterView?: (viewId: string) => void;
	showAdvancedFilters?: boolean;
	onToggleAdvancedFilters?: (show: boolean) => void;
}

export const HeaderSection = ({
	view,
	onViewChange,
	onNewCustomer,
	onManageFields,
	onCustomersTab,
	onAdvancedFilters,
	searchValue,
	onSearchChange,
	groupBy,
	onGroupByChange,
	groupByOptions = [],
	activeTab = ETabs.CUSTOMERS,
	filterViews = [],
	activeFilterView,
	onFilterViewChange,
	onCloseFilterView,
	showAdvancedFilters = false,
	onToggleAdvancedFilters,
}: ExtendedHeaderSectionProps) => {
	const handleTabClick = (tabName: ETabs): void => {
		if (tabName === ETabs.FIELDS) {
			onManageFields();
		} else if (tabName === ETabs.CUSTOMERS && onCustomersTab) {
			onCustomersTab();
		}
	};

	const handleFilterViewClick = (viewId: string) => {
		const newActiveView = activeFilterView === viewId ? null : viewId;
		onFilterViewChange?.(newActiveView);

		if (showAdvancedFilters && !newActiveView) {
			onToggleAdvancedFilters?.(false);
		}
	};

	const handleCloseFilterView = (e: React.MouseEvent, viewId: string) => {
		e.stopPropagation();
		onCloseFilterView?.(viewId);

		if (activeFilterView === viewId && showAdvancedFilters) {
			onToggleAdvancedFilters?.(false);
		}
	};

	const handleAdvancedFiltersClick = () => {
		if (onToggleAdvancedFilters) {
			onToggleAdvancedFilters(!showAdvancedFilters);
		} else {
			onAdvancedFilters();
		}
	};

	const handleCustomersTabClick = () => {
		handleTabClick(ETabs.CUSTOMERS);
		onFilterViewChange?.(null);
		if (showAdvancedFilters) {
			onToggleAdvancedFilters?.(false);
		}
	};

	const getGroupByOptions = () => {
		if (view === EViewType.BOARD) {
			return groupByOptions;
		}
		return [{ value: '', label: 'All Groups' }, ...groupByOptions];
	};

	return (
		<div className="bg-white p-6">
			<div className="flex justify-between items-center mb-6">
				<div className="flex space-x-8">
					<div
						className={`flex items-center pb-2 border-b-2 cursor-pointer ${
							activeTab === ETabs.CUSTOMERS && !activeFilterView
								? 'border-blue-500'
								: 'border-transparent opacity-60 hover:opacity-100'
						}`}
						onClick={handleCustomersTabClick}
					>
						<UserOutlined
							className={`mr-2 ${
								activeTab === ETabs.CUSTOMERS && !activeFilterView
									? 'text-blue-500'
									: 'text-gray-500'
							}`}
						/>
						<Text
							className={`font-semibold ${
								activeTab === ETabs.CUSTOMERS && !activeFilterView
									? 'text-blue-500'
									: 'text-gray-500'
							}`}
						>
							My Customers
						</Text>
					</div>

					{filterViews.map((filterView) => (
						<div
							key={filterView.id}
							className={`flex items-center pb-2 border-b-2 cursor-pointer group ${
								activeFilterView === filterView.id
									? 'border-orange-500'
									: 'border-transparent opacity-60 hover:opacity-100'
							}`}
							onClick={() => handleFilterViewClick(filterView.id)}
						>
							<FilterOutlined
								className={`mr-2 ${
									activeFilterView === filterView.id ? 'text-orange-500' : 'text-gray-500'
								}`}
							/>
							<Text
								className={`font-semibold ${
									activeFilterView === filterView.id ? 'text-orange-500' : 'text-gray-500'
								}`}
							>
								{filterView.name}
							</Text>
							{/* {filterView.customerCount !== undefined && (
								<span className="ml-1 text-xs text-gray-400">({filterView.customerCount})</span>
							)} */}
							<Button
								type="text"
								size="small"
								icon={<CloseOutlined />}
								className="ml-2 text-red-500 opacity-0 group-hover:opacity-100 transition-opacity"
								onClick={(e) => handleCloseFilterView(e, filterView.id)}
							/>
						</div>
					))}

					<div
						className={`flex items-center pb-2 border-b-2 cursor-pointer ${
							activeTab === ETabs.FIELDS
								? 'border-blue-500'
								: 'border-transparent opacity-60 hover:opacity-100'
						}`}
						onClick={() => handleTabClick(ETabs.FIELDS)}
					>
						<SettingOutlined
							className={`mr-2 ${activeTab === ETabs.FIELDS ? 'text-blue-500' : 'text-gray-500'}`}
						/>
						<Text
							className={`font-semibold ${
								activeTab === ETabs.FIELDS ? 'text-blue-500' : 'text-gray-500'
							}`}
						>
							Customer Info Fields
						</Text>
					</div>
				</div>
			</div>

			<div className="flex justify-between items-center mb-4">
				<div className="flex items-center space-x-4">
					{(activeTab === ETabs.CUSTOMERS || activeFilterView) && (
						<div className="flex bg-gray-100 rounded-md p-1">
							<Button
								type={view === EViewType.BOARD ? 'primary' : 'default'}
								icon={<AppstoreOutlined />}
								onClick={() => onViewChange(EViewType.BOARD)}
								className={`border-none ${
									view === EViewType.BOARD
										? 'bg-blue-600 text-white shadow-sm'
										: 'bg-transparent text-gray-600 hover:bg-gray-50'
								}`}
							>
								Board
							</Button>
							<Button
								type={view === EViewType.TABLE ? 'primary' : 'default'}
								icon={<UnorderedListOutlined />}
								onClick={() => onViewChange(EViewType.TABLE)}
								className={`border-none ${
									view === EViewType.TABLE
										? 'bg-blue-600 text-white shadow-sm'
										: 'bg-transparent text-gray-600 hover:bg-gray-50'
								}`}
							>
								List
							</Button>
						</div>
					)}

					{(activeTab === ETabs.CUSTOMERS || activeFilterView) && (
						<Button
							type="primary"
							icon={<PlusOutlined />}
							onClick={onNewCustomer}
							className="bg-blue-600 border-blue-600 hover:bg-blue-700"
						>
							New Customer
						</Button>
					)}
				</div>
			</div>

			{(activeTab === ETabs.CUSTOMERS || activeFilterView) && (
				<div className="flex items-center gap-4">
					<div className="flex-1 max-w-md">
						<Search
							placeholder={PLACEHOLDERS.SEARCH_CUSTOMERS}
							value={searchValue}
							onChange={(e) => onSearchChange(e.target.value)}
							onSearch={onSearchChange}
							allowClear
							className="w-full"
						/>
					</div>

					{!activeFilterView && (
						<Select
							value={groupBy}
							onChange={onGroupByChange}
							className="w-40"
							options={getGroupByOptions()}
							placeholder="Group by field"
						/>
					)}

					<div className="flex items-center space-x-3">
						<Button
							icon={<FilterOutlined />}
							onClick={handleAdvancedFiltersClick}
							className="border-gray-300"
							type={showAdvancedFilters ? 'primary' : 'default'}
						>
							Advanced Filters
						</Button>

						<Button type="text" icon={<InfoCircleOutlined />} className="text-gray-400" />
					</div>
				</div>
			)}
		</div>
	);
};
